import React, { useEffect, useMemo, useState } from 'react'
import { formatEssayShortDate } from 'pages/ApplicationDetailsPage/components/Essay/utils/formatEssayDate'
import { mapEssayVersionStatusLabel } from 'pages/ApplicationDetailsPage/components/Essay/utils/essayVersionStatus'
import { splitVersionsByDraft } from 'shared/essayReview/essayReviewModel'
import { ReactComponent as ChevronIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as MessagesIcon } from 'assets/icons/message-full.svg'
import s from './EssayVersionDropdown.module.scss'

const UnreadBadge = ({ count }) => {
	if (!count || count <= 0) return null
	return (
		<span className={s.badge}>
			<span className={s.badge__text}>+{count}</span>
			<span className={s.badge__icon} aria-hidden>
				<MessagesIcon />
			</span>
		</span>
	)
}

const VersionOption = ({ version, activeVersionId, onSelect, showBorder }) => {
	const isActive = String(version.id) === String(activeVersionId)
	const dateLabel = formatEssayShortDate(version.updatedAt)

	return (
		<button
			type="button"
			className={[
				s.row,
				showBorder ? s.rowBorder : '',
				isActive ? s.rowActive : '',
				version.isDraft ? s.draft : '',
			]
				.filter(Boolean)
				.join(' ')}
			onClick={() => onSelect(version.id)}
		>
			<span className={s.rowMain}>
				<span className={s.rowTitle}>
					Version {version.versionNumber} ·{' '}
					{version.isDraft
						? 'Draft'
						: mapEssayVersionStatusLabel(version.status)}
				</span>
				{dateLabel ? (
					<span className={s.rowDate}>Updated {dateLabel}</span>
				) : null}
			</span>
			<UnreadBadge count={version.unreadCounselorCount} />
		</button>
	)
}

const EssayVersionDropdown = ({
	versions = [],
	activeVersionId,
	onSelect,
	disabled = false,
}) => {
	const [open, setOpen] = useState(false)
	const [draftsOpen, setDraftsOpen] = useState(false)

	const active = versions.find(v => String(v.id) === String(activeVersionId))
	const unreadActive = versions.reduce(
		(sum, v) => sum + (Number(v.unreadCounselorCount) || 0),
		0,
	)

	const { priority, drafts } = useMemo(
		() => splitVersionsByDraft(versions),
		[versions],
	)

	useEffect(() => {
		if (!open) setDraftsOpen(false)
	}, [open])

	const label = active ? `Version ${active.versionNumber}` : 'Version'

	const handleSelect = versionId => {
		onSelect?.(versionId)
		setOpen(false)
	}

	return (
		<div className={s.wrap}>
			<button
				type="button"
				className={s.trigger}
				disabled={disabled || !versions.length}
				onClick={() => setOpen(v => !v)}
				aria-expanded={open}
			>
				<span>{label}</span>
				<span className={`${s.chevron} ${open ? s.chevronOpen : ''}`}>
					<ChevronIcon />
				</span>
				<UnreadBadge count={unreadActive} />
			</button>
			{open ? (
				<div className={s.menu} role="listbox">
					{priority.map((v, index) => (
						<VersionOption
							key={v.id}
							version={v}
							activeVersionId={activeVersionId}
							onSelect={handleSelect}
							showBorder={index > 0}
						/>
					))}
					{drafts.length > 0 ? (
						<>
							<button
								type="button"
								className={[s.draftsToggle, priority.length > 0 ? s.rowBorder : '']
									.filter(Boolean)
									.join(' ')}
								onClick={() => setDraftsOpen(v => !v)}
								aria-expanded={draftsOpen}
							>
								<span className={s.draftsToggleLabel}>
									Draft +{drafts.length}
								</span>
								<span
									className={`${s.chevron} ${draftsOpen ? s.chevronOpen : ''}`}
								>
									<ChevronIcon />
								</span>
							</button>
							{draftsOpen
								? drafts.map(v => (
										<VersionOption
											key={v.id}
											version={v}
											activeVersionId={activeVersionId}
											onSelect={handleSelect}
											showBorder
										/>
									))
								: null}
						</>
					) : null}
				</div>
			) : null}
		</div>
	)
}

export default EssayVersionDropdown
